import { PROFILE_VIEW_SECTION } from '@workspace/seedforge-shared'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  HeartbeatInstrumentation,
  ProfileTrackingConfig,
} from '../profile/profile-editable-view.js'

export type { HeartbeatInstrumentation, ProfileTrackingConfig }

const HEARTBEAT_INTERVAL = 5_000
const IDLE_TIMEOUT = 30_000 // Consider idle after 30s of no activity

/**
 * Fraction of the viewport height used as the "what am I reading" line. Slightly
 * above centre: readers track content in the upper half of the screen.
 */
const SECTION_ANCHOR = 0.4

/**
 * Which marked section the visitor is looking at, measured against the live DOM
 * at call time.
 *
 * Deliberately not an IntersectionObserver. An observer has to be handed its
 * targets up front, so sections that mount later (everything behind the interest
 * gate) would never be watched and their time would be credited to whichever
 * section was last seen. Observers also rank by `intersectionRatio`, which is a
 * fraction of the *element*, so a short section fully on screen (1.0) beats a
 * tall one filling the viewport (~0.3) — exactly backwards.
 *
 * Assumes no marked element contains another; see the nesting test in
 * profile-tracking.test.ts.
 */
export function resolveActiveSection(container: HTMLElement): string | undefined {
  const viewportHeight = window.innerHeight
  const anchorY = viewportHeight * SECTION_ANCHOR
  let best: { section: string; visible: number } | undefined

  for (const el of container.querySelectorAll<HTMLElement>('[data-section]')) {
    const section = el.dataset.section
    if (!section) continue
    const rect = el.getBoundingClientRect()
    if (rect.height === 0) continue

    // Straddling the anchor line wins outright — that is what is being read.
    if (rect.top <= anchorY && rect.bottom >= anchorY) return section

    // Otherwise fall back to whichever covers the most viewport, so a visitor
    // parked above or below every anchor still attributes somewhere sensible.
    const visible = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0))
    if (!best || visible > best.visible) best = { section, visible }
  }

  return best?.section
}

/** One-shot device/browser signals — unchanging for the life of the page load. */
function captureStaticInstrumentation(): Omit<
  HeartbeatInstrumentation,
  'firstInputAt' | 'inputCount'
> {
  return {
    userAgent: navigator.userAgent,
    browserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    isWebdriver: navigator.webdriver === true,
    referrer: document.referrer || undefined,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    language: navigator.language,
  }
}

/**
 * Hook that sends heartbeats every 5 seconds while the investor is actively
 * viewing the page. Activity is detected via mousemove, scroll, click, keypress,
 * and touch events. Heartbeat pauses when idle or page is hidden.
 */
export function useProfileTracker(tracking: ProfileTrackingConfig | undefined) {
  const containerRef = useRef<HTMLDivElement>(null)
  const trackingRef = useRef(tracking)
  trackingRef.current = tracking
  const lastActivityRef = useRef(Date.now())
  const [ready, setReady] = useState(false)
  const [isIdle, setIsIdle] = useState(false)
  const staticInstrumentation = useMemo(() => captureStaticInstrumentation(), [])
  const firstInputAtRef = useRef<Date | null>(null)
  const inputCountRef = useRef(0)

  const setRef = useCallback((node: HTMLDivElement | null) => {
    ;(containerRef as React.MutableRefObject<HTMLDivElement | null>).current = node
    if (node) setReady(true)
  }, [])

  useEffect(() => {
    if (!ready || !containerRef.current) return

    // Track user activity
    const markActive = () => {
      lastActivityRef.current = Date.now()
      setIsIdle(false)
      inputCountRef.current += 1
      if (!firstInputAtRef.current) firstInputAtRef.current = new Date()
    }
    const activityEvents = ['mousemove', 'scroll', 'click', 'keydown', 'touchstart'] as const
    for (const event of activityEvents) {
      document.addEventListener(event, markActive, { passive: true })
    }

    const sendHeartbeat = () => {
      const t = trackingRef.current
      if (!t) return
      if (document.visibilityState === 'hidden') return

      // Skip if investor is idle (no activity for IDLE_TIMEOUT)
      const idleMs = Date.now() - lastActivityRef.current
      if (idleMs > IDLE_TIMEOUT) {
        setIsIdle(true)
        return
      }

      const instrumentation: HeartbeatInstrumentation = {
        ...staticInstrumentation,
        firstInputAt: firstInputAtRef.current ?? undefined,
        inputCount: inputCountRef.current,
      }

      const visibleSection = containerRef.current
        ? resolveActiveSection(containerRef.current)
        : undefined

      // Open content wins over whatever is scrolled behind it. `activeVideo` is
      // the modal-open flag, NOT "this profile has a pitch" — conflating the two
      // routes every heartbeat to 'pitch' and records no section time at all.
      if (t.activeDocument) {
        const page = t.documentPage
        t.onHeartbeat?.('document', t.activeDocument, page?.page, page?.totalPages, instrumentation)
      } else if (t.activeVideo) {
        t.onHeartbeat?.('pitch', t.activeVideoId, undefined, undefined, instrumentation)
      } else if (t.deckDocumentId && visibleSection === PROFILE_VIEW_SECTION.DECK) {
        // Reading the deck inline (not through the modal previewer) is the
        // common case — without this, page-level reading time was only ever
        // recorded for the rare modal-open path.
        const page = t.documentPage
        t.onHeartbeat?.('document', t.deckDocumentId, page?.page, page?.totalPages, instrumentation)
      } else {
        // The section rides in resourceId; `section` stays 'profile' so the
        // existing three-value breakdowns keep aggregating correctly.
        t.onHeartbeat?.('profile', visibleSection, undefined, undefined, instrumentation)
      }
    }

    // Fires immediately: this is the only call that carries instrumentation into
    // session creation (trackEvent does not), so dropping it would leave
    // userAgent/ipHash/isLikelyHeadless permanently null on sessions whose first
    // interaction is an event rather than a heartbeat.
    sendHeartbeat()
    const interval = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL)

    return () => {
      clearInterval(interval)
      for (const event of activityEvents) {
        document.removeEventListener(event, markActive)
      }
    }
    // Intentionally excludes `tracking`: the config is read through trackingRef,
    // which is reassigned every render. Adding it here would tear down and
    // restart the interval on every reaction/state change, and each restart
    // re-fires the immediate heartbeat above — spraying phantom 5s credits.
  }, [ready, staticInstrumentation])

  return { setRef, isIdle }
}
